import React, { useState } from 'react';
import styled from 'styled-components';
import { X, Shield, AlertTriangle, Info, CheckCircle } from 'lucide-react';

const theme = {
  colors: {
    primary: '#3b82f6',
    primaryLight: '#eff6ff',
    success: '#16a34a',
    warning: '#f59e0b',
    error: '#dc2626',
    info: '#3b82f6',
    textHeading: '#0f172a',
    textBody: '#374151',
    textMuted: '#6b7280',
    background: '#ffffff',
    border: '#e5e7eb',
  },
  shadows: {
    sm: '0 1px 2px rgba(15, 23, 42, 0.06)',
    md: '0 4px 12px rgba(15, 23, 42, 0.08)',
  },
  radius: '12px',
};

const TipContainer = styled.div`
  background: ${theme.colors.background};
  border: 1px solid ${({ $type }) => {
    switch ($type) {
      case 'warning': return '#fed7aa';
      case 'error': return '#fecaca';
      case 'success': return '#bbf7d0';
      case 'info': return '#dbeafe';
      default: return theme.colors.border;
    }
  }};
  border-radius: ${theme.radius};
  padding: 16px;
  margin-bottom: 12px;
  position: relative;
  box-shadow: ${theme.shadows.sm};
  transition: all 0.2s ease;

  &:hover {
    box-shadow: ${theme.shadows.md};
    transform: translateY(-1px);
  }
`;

const TipHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: ${({ $hasContent }) => $hasContent ? '8px' : '0'};
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ $type }) => {
    switch ($type) {
      case 'warning': return '#fef3c7';
      case 'error': return '#fee2e2';
      case 'success': return '#dcfce7';
      case 'info': return '#dbeafe';
      default: return theme.colors.primaryLight;
    }
  }};
  color: ${({ $type }) => {
    switch ($type) {
      case 'warning': return '#f59e0b';
      case 'error': return '#dc2626';
      case 'success': return '#16a34a';
      case 'info': return '#3b82f6';
      default: return theme.colors.primary;
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
`;

const TipContent = styled.div`
  flex: 1;
`;

const TipTitle = styled.h4`
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.textHeading};
  line-height: 1.3;
`;

const TipText = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${theme.colors.textBody};
  line-height: 1.5;
`;

const DismissButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  color: ${theme.colors.textMuted};
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.border};
    color: ${theme.colors.textBody};
  }
`;

const PriorityBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: ${({ $priority }) => {
    switch ($priority) {
      case 'high': return '#fee2e2';
      case 'medium': return '#fef3c7';
      default: return '#f3f4f6';
    }
  }};
  color: ${({ $priority }) => {
    switch ($priority) {
      case 'high': return '#dc2626';
      case 'medium': return '#f59e0b';
      default: return '#6b7280';
    }
  }};
  font-size: 11px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 8px;
`;

const CompactContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${({ $type }) => {
    switch ($type) {
      case 'warning': return '#fffbeb';
      case 'error': return '#fef2f2';
      case 'success': return '#f0fdf4';
      case 'info': return '#eff6ff';
      default: return theme.colors.primaryLight;
    }
  }};
  border: 1px solid ${({ $type }) => {
    switch ($type) {
      case 'warning': return '#fed7aa';
      case 'error': return '#fecaca';
      case 'success': return '#bbf7d0';
      case 'info': return '#dbeafe';
      default: return theme.colors.border;
    }
  }};
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  color: ${theme.colors.textBody};
  position: relative;
`;

const SafetyTip = ({ 
  tip, 
  type = 'info', 
  compact = false, 
  dismissible = false, 
  onDismiss,
  showPriority = false 
}) => {
  const [isDismissed, setIsDismissed] = useState(false);

  const handleDismiss = () => {
    setIsDismissed(true);
    if (onDismiss) {
      onDismiss(tip.id);
    }
  };

  if (isDismissed) {
    return null;
  }

  const getIcon = () => {
    switch (type) {
      case 'warning':
        return <AlertTriangle size={14} />;
      case 'error':
        return <AlertTriangle size={14} />;
      case 'success':
        return <CheckCircle size={14} />;
      case 'info':
        return <Info size={14} />;
      default:
        return <Shield size={14} />;
    }
  };

  if (compact) {
    return (
      <CompactContainer $type={type}>
        <IconWrapper $type={type} style={{ width: '16px', height: '16px' }}>
          {tip.icon || getIcon()}
        </IconWrapper>
        <span>{tip.title}</span>
        {dismissible && (
          <DismissButton onClick={handleDismiss} style={{ position: 'static', padding: '2px' }}>
            <X size={12} />
          </DismissButton>
        )}
      </CompactContainer>
    );
  }

  return (
    <TipContainer $type={type}>
      <TipHeader $hasContent={!!tip.content}>
        <IconWrapper $type={type}>
          {tip.icon || getIcon()}
        </IconWrapper>
        <TipContent>
          <TipTitle>
            {tip.title}
            {showPriority && tip.priority && (
              <PriorityBadge $priority={tip.priority}>
                {tip.priority}
              </PriorityBadge>
            )}
          </TipTitle>
          {tip.content && (
            <TipText>{tip.content}</TipText>
          )}
        </TipContent>
      </TipHeader>
      {dismissible && (
        <DismissButton onClick={handleDismiss}>
          <X size={16} />
        </DismissButton>
      )}
    </TipContainer>
  );
};

export default SafetyTip;